// client/src/pages/Game/Battlefield/utils/targetReticle.js
//
// R34ll : réticule de ciblage pour le drag des cartes en main.
//
// Au début d'un drag sur une carte de la main :
//  - une CIBLE (réticule animé) apparaît sous le doigt du joueur ;
//  - une LIGNE POINTILLÉE relie la carte source (dans la main) au réticule ;
//  - le réticule donne un feedback temps réel :
//      • cible ÉLIGIBLE  → tourne dans un sens, grossit, devient VERT
//      • cible REFUSÉE   → tourne dans l'autre sens, grossit, devient ROUGE
//      • ailleurs        → reste neutre (cyan discret)
//  - `dismiss()` joue une animation de disparition (implosion) au relâcher.
//
// Tout est en `position:fixed; pointer-events:none` : le réticule
// n'interfère jamais avec le hit-testing (document.elementsFromPoint).

const SVG_NS = 'http://www.w3.org/2000/svg';

const COLORS = {
  neutral: '#5acdff',
  valid: '#2ee66b',
  invalid: '#ff4d5e',
};

/**
 * Crée le réticule + la ligne pointillée.
 * @param {{sourceEl: HTMLElement, color?: string}} opts
 * @returns {controller} { moveTo, setFeedback, dismiss, destroy }
 */
export function createTargetReticle({ sourceEl }) {
  let x = 0, y = 0;
  let feedback = 'neutral';
  let dismissed = false;
  let destroyTimer = null;

  // ── Ligne pointillée (SVG plein écran, sous le réticule) ──
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', 'target-reticle-svg');
  svg.style.cssText =
    'position:fixed;left:0;top:0;width:100vw;height:100vh;pointer-events:none;z-index:10040;';
  const line = document.createElementNS(SVG_NS, 'line');
  line.setAttribute('stroke', COLORS.neutral);
  line.setAttribute('stroke-width', '2.5');
  line.setAttribute('stroke-dasharray', '7 8');
  line.setAttribute('stroke-linecap', 'round');
  const lineAnim = document.createElementNS(SVG_NS, 'animate');
  lineAnim.setAttribute('attributeName', 'stroke-dashoffset');
  lineAnim.setAttribute('from', '0');
  lineAnim.setAttribute('to', '-30');
  lineAnim.setAttribute('dur', '0.7s');
  lineAnim.setAttribute('repeatCount', 'indefinite');
  line.appendChild(lineAnim);
  svg.appendChild(line);
  document.body.appendChild(svg);

  // Petit halo au point d'ancrage (sur la carte source)
  const anchor = document.createElement('div');
  anchor.className = 'target-reticle-anchor';
  anchor.style.cssText =
    'position:fixed;pointer-events:none;z-index:10041;transform:translate(-50%,-50%);' +
    'width:14px;height:14px;border-radius:50%;' +
    `background:radial-gradient(circle, ${COLORS.neutral}cc 0%, transparent 70%);` +
    'box-shadow:0 0 12px #5acdff88;';
  document.body.appendChild(anchor);

  // ── Réticule (la cible sous le doigt) ──
  const reticle = document.createElement('div');
  reticle.className = 'target-reticle';
  reticle.style.cssText =
    'position:fixed;pointer-events:none;z-index:10042;' +
    'width:84px;height:84px;transform:translate(-50%,-50%);' +
    'will-change:transform,left,top;';

  // Anneau externe (cercle)
  const ring = document.createElement('div');
  ring.className = 'target-reticle-ring';
  ring.style.cssText =
    'position:absolute;inset:0;border-radius:50%;' +
    `border:3px solid ${COLORS.neutral};` +
    'box-shadow:0 0 14px currentColor, inset 0 0 10px currentColor;' +
    'color:#5acdff;';
  reticle.appendChild(ring);

  // Anneau interne en rotation continue (légère, sens horaire)
  const tickRing = document.createElement('div');
  tickRing.className = 'target-reticle-ticks';
  tickRing.style.cssText =
    'position:absolute;inset:9px;border-radius:50%;' +
    'background:conic-gradient(from 0deg, currentColor 0 14deg, transparent 14deg 76deg, ' +
    'currentColor 76deg 90deg, transparent 90deg 152deg, currentColor 152deg 166deg, ' +
    'transparent 166deg 228deg, currentColor 228deg 242deg, transparent 242deg 304deg, ' +
    'currentColor 304deg 318deg, transparent 318deg 360deg);' +
    '-webkit-mask:radial-gradient(circle, transparent 58%, #000 60% 82%, transparent 84%);' +
    'mask:radial-gradient(circle, transparent 58%, #000 60% 82%, transparent 84%);' +
    'animation:target-reticle-spin 2.4s linear infinite;color:#5acdff;';
  reticle.appendChild(tickRing);

  // Croix centrale
  const dot = document.createElement('div');
  dot.className = 'target-reticle-dot';
  dot.style.cssText =
    'position:absolute;left:50%;top:50%;width:10px;height:10px;' +
    'transform:translate(-50%,-50%);border-radius:50%;' +
    'background:#fff;box-shadow:0 0 10px currentColor;color:#5acdff;';
  reticle.appendChild(dot);

  document.body.appendChild(reticle);

  // Apparition (pop-in)
  reticle.animate(
    [
      { transform: 'translate(-50%,-50%) scale(0.2)', opacity: 0 },
      { transform: 'translate(-50%,-50%) scale(1.18)', opacity: 1, offset: 0.7 },
      { transform: 'translate(-50%,-50%) scale(1)', opacity: 1 },
    ],
    { duration: 260, easing: 'cubic-bezier(0.2,0.9,0.3,1.3)', fill: 'forwards' }
  );
  svg.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, fill: 'forwards' });

  // ── Position de la source (recalculée à chaque move : la main peut se
  // réorganiser pendant le drag) ──
  const sourceCenter = () => {
    const r = sourceEl && sourceEl.isConnected
      ? sourceEl.getBoundingClientRect()
      : null;
    return r
      ? { x: r.left + r.width / 2, y: r.top + r.height / 2 }
      : { x: window.innerWidth / 2, y: window.innerHeight - 100 };
  };

  const apply = () => {
    const c = sourceCenter();
    anchor.style.left = `${c.x}px`;
    anchor.style.top = `${c.y}px`;
    line.setAttribute('x1', String(c.x));
    line.setAttribute('y1', String(c.y));
    line.setAttribute('x2', String(x));
    line.setAttribute('y2', String(y));
    line.setAttribute('stroke', COLORS[feedback]);
    reticle.style.left = `${x}px`;
    reticle.style.top = `${y}px`;
  };

  // ── API du contrôleur ──
  const controller = {
    /** Déplace le réticule sous le pointeur. */
    moveTo(px, py) {
      if (dismissed) return;
      x = px; y = py;
      apply();
    },

    /**
     * Feedback : 'valid' (vert) | 'invalid' (rouge) | 'neutral'.
     * Vert  : rotation horaire + grossit (scale 1.22) + glow vert.
     * Rouge : rotation anti-horaire + grossit (scale 1.22) + glow rouge.
     */
    setFeedback(next) {
      if (dismissed || next === feedback) return;
      feedback = next;
      const col = COLORS[feedback];
      ring.style.borderColor = col;
      ring.style.color = col;
      tickRing.style.color = col;
      dot.style.color = col;
      line.setAttribute('stroke', col);
      anchor.style.background = `radial-gradient(circle, ${col}cc 0%, transparent 70%)`;
      anchor.style.boxShadow = `0 0 12px ${col}88`;

      const scale = feedback === 'neutral' ? 1 : 1.22;
      const rot = feedback === 'valid' ? '135deg' : feedback === 'invalid' ? '-135deg' : '0deg';
      reticle.animate(
        [
          { transform: 'translate(-50%,-50%) scale(1) rotate(0deg)' },
          { transform: `translate(-50%,-50%) scale(${scale}) rotate(${rot})` },
        ],
        { duration: 300, easing: 'cubic-bezier(0.2,0.9,0.3,1.2)', fill: 'forwards' }
      );
      // Onde de confirmation (halo qui s'étend)
      if (feedback !== 'neutral') {
        const pulse = document.createElement('div');
        pulse.style.cssText =
          `position:absolute;inset:-8px;border-radius:50%;border:2px solid ${col};` +
          'opacity:0.9;pointer-events:none;';
        reticle.appendChild(pulse);
        pulse.animate(
          [
            { transform: 'scale(0.8)', opacity: 0.9 },
            { transform: 'scale(1.8)', opacity: 0 },
          ],
          { duration: 450, easing: 'ease-out', fill: 'forwards' }
        ).onfinish = () => pulse.remove();
      }
    },

    /** Animation de disparition au relâcher, puis suppression du DOM. */
    dismiss(onDone) {
      if (dismissed) { if (onDone) onDone(); return; }
      dismissed = true;
      svg.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160, fill: 'forwards' });
      anchor.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 160, fill: 'forwards' });
      const anim = reticle.animate(
        [
          { transform: 'translate(-50%,-50%) scale(1)', opacity: 1 },
          { transform: 'translate(-50%,-50%) scale(1.45)', opacity: 0.75, offset: 0.45 },
          { transform: 'translate(-50%,-50%) scale(0.05)', opacity: 0 },
        ],
        { duration: 280, easing: 'cubic-bezier(0.5,0,0.8,0.4)', fill: 'forwards' }
      );
      anim.onfinish = () => {
        controller.destroy();
        if (onDone) onDone();
      };
      // Sécurité : jamais plus de 500 ms
      destroyTimer = setTimeout(() => { controller.destroy(); if (onDone) onDone(); }, 480);
    },

    /** Suppression immédiate (annulation, démontage). */
    destroy() {
      dismissed = true;
      if (destroyTimer) { clearTimeout(destroyTimer); destroyTimer = null; }
      reticle.remove(); svg.remove(); anchor.remove();
    },

    get feedback() { return feedback; },
  };

  return controller;
}

// ── Keyframes injectées une seule fois ──
let keyframesInjected = false;
export function ensureReticleStyles() {
  if (keyframesInjected || document.getElementById('target-reticle-styles')) return;
  const style = document.createElement('style');
  style.id = 'target-reticle-styles';
  style.textContent = `
@keyframes target-reticle-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.hand-card.reticle-drag-source {
  filter: brightness(1.28) drop-shadow(0 0 14px #5acdffaa) drop-shadow(0 10px 18px rgba(0,0,0,0.55));
  z-index: 900 !important;
}
.hand-card.reticle-drag-invalid {
  animation: target-reticle-shake 0.4s ease;
  filter: brightness(1.15) drop-shadow(0 0 14px #ff4d5eaa);
}
.hand-card-unaffordable {
  animation: target-unaffordable-red 0.45s ease 2;
}
@keyframes target-unaffordable-red {
  0%, 100% { filter: brightness(1); box-shadow: 0 0 0 rgba(255,60,70,0); }
  50% { filter: brightness(1.5) saturate(1.4); box-shadow: 0 0 22px 4px rgba(255,60,70,0.85); }
}
.hand-card-playable-glow {
  animation: target-playable-shine 0.6s ease 2;
}
@keyframes target-playable-shine {
  0%, 100% { filter: brightness(1); }
  50% { filter: brightness(1.7) drop-shadow(0 0 12px rgba(255,255,255,0.95)) saturate(1.3); }
}
@keyframes target-reticle-shake {
  0%, 100% { translate: 0 0; }
  20% { translate: -7px 0; }
  40% { translate: 6px 0; }
  60% { translate: -4px 0; }
  80% { translate: 3px 0; }
}
`;
  document.head.appendChild(style);
  keyframesInjected = true;
}
